import React from "react";
import { Input, Select, Radio, Switch, Checkbox, Upload, Row, Col, Typography, Divider, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { MIN_IMAGES } from "./validators";
import OrganizerSelect from "./OrganizerSelect";

const { TextArea } = Input;
const { Title, Text } = Typography;

// Matches the backend's per-request multer limit (multer.array("images", 10))
// — more than MIN_IMAGES is fine, but a single upload batch can't exceed this.
const MAX_IMAGES = 10;
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

const Label = ({ children, required, error }) => (
  <div style={{ marginBottom: 8, fontWeight: 500 }}>
    {children}
    {required ? " *" : ""}
    {error && (
      <span style={{ color: "#ff4d4f", fontWeight: 400 }}> — {error}</span>
    )}
  </div>
);

/**
 * Step 1 — Basics: list type, title, description, images, tags, location.
 * `isEdit` locks the list-type toggle (switching types after creation would
 * mean a different backend model entirely).
 */
const BasicInfoStep = ({
  form,
  setForm,
  isEdit,
  errors = {},
  pendingImageFiles,
  setPendingImageFiles,
}) => {
  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const updateLocation = (patch) =>
    setForm((prev) => ({ ...prev, location: { ...prev.location, ...patch } }));

  const imageFileList = [
    ...form.imageUrls.map((url, i) => ({
      uid: `existing-${i}`,
      name: `image-${i + 1}`,
      status: "done",
      url,
    })),
    ...pendingImageFiles.map((file, i) => ({
      uid: `pending-${i}`,
      name: file.name,
      status: "done",
      url: URL.createObjectURL(file),
      originFileObj: file,
    })),
  ];

  const handleImageChange = ({ fileList }) => {
    const nextExisting = [];
    const nextPending = [];
    fileList.forEach((item) => {
      if (item.originFileObj) nextPending.push(item.originFileObj);
      else if (item.url) nextExisting.push(item.url);
    });
    update({ imageUrls: nextExisting });
    setPendingImageFiles(nextPending);
  };

  return (
    <div>
      <Title level={4}>Basics</Title>
      <Text type="secondary">
        What is this listing, and what does it look like to a traveler?
      </Text>

      <Divider />

      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 8, fontWeight: 500 }}>List Type *</div>
        <Radio.Group
          disabled={isEdit}
          value={form.listType}
          onChange={(e) => update({ listType: e.target.value })}
        >
          <Radio.Button value="PACKAGE">Packages</Radio.Button>
          <Radio.Button value="QUICK_TRIP">Quick Trips</Radio.Button>
        </Radio.Group>
      </div>

      <Row gutter={16}>
        <Col span={12}>
          <Label required error={errors.title}>List Name</Label>
          <Input
            status={errors.title ? "error" : undefined}
            placeholder="e.g. Zanzibar Escape"
            value={form.title}
            onChange={(e) => update({ title: e.target.value })}
          />
        </Col>
        {/* <Col span={12}>
          <Label required error={errors.locationName}>Destination</Label>
          <Input
            status={errors.locationName ? "error" : undefined}
            placeholder="e.g. Zanzibar, Tanzania"
            value={form.location.name}
            onChange={(e) => updateLocation({ name: e.target.value })}
          />
        </Col> */}
      </Row>

      <div style={{ marginTop: 16 }}>
        <Label>Organizer</Label>
        <OrganizerSelect
          value={form.organizerId}
          onChange={(organizerId) => update({ organizerId })}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          The partner this {form.listType === "PACKAGE" ? "package" : "quick trip"} belongs
          to. Leave empty for a Tramango listing.
        </Text>
      </div>

      <div style={{ margin: "16px 0" }}>
        <div style={{ marginBottom: 8, fontWeight: 500 }}>
          List Description
        </div>
        <TextArea
          rows={4}
          maxLength={1000}
          showCount
          placeholder="Describe the listing"
          value={form.description}
          onChange={(e) => update({ description: e.target.value })}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <Label required error={errors.tags}>Tags (audience & activity)</Label>
        <Select
          mode="tags"
          status={errors.tags ? "error" : undefined}
          style={{ width: "100%" }}
          placeholder="e.g. Couples, Adventure, Beach — press enter to add"
          value={form.tags}
          onChange={(tags) => update({ tags })}
          tokenSeparators={[","]}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <Label required error={errors.imageUrls}>
          Images ({imageFileList.length}, min {MIN_IMAGES})
        </Label>
        <Upload
          listType="picture-card"
          fileList={imageFileList}
          maxCount={MAX_IMAGES}
          multiple
          accept=".jpg,.jpeg,.png,.gif,.webp"
          beforeUpload={(file) => {
            if (file.size > MAX_IMAGE_BYTES) {
              message.error("Each image must be 2MB or smaller");
              return Upload.LIST_IGNORE;
            }
            return false;
          }}
          onChange={handleImageChange}
        >
          {imageFileList.length >= MAX_IMAGES ? null : (
            <div>
              <PlusOutlined />
              <div style={{ marginTop: 8 }}>Upload</div>
            </div>
          )}
        </Upload>
        <Text type="secondary" style={{ fontSize: 12 }}>
          At least {MIN_IMAGES} images required, 2MB each, up to {MAX_IMAGES}{" "}
          per upload batch (add more later if you need beyond that). New
          uploads are attached right after you save this{" "}
          {form.listType === "PACKAGE" ? "package" : "quick trip"}.
        </Text>
      </div>

      <Title level={5} style={{ marginTop: 24 }}>
        Location detail
      </Title>
      <Row gutter={16} style={{ marginBottom: 8 }}>
        <Col span={12}>
          <Input
            status={errors.locationName ? "error" : undefined}
            placeholder="Destination Name (e.g. Zanzibar, Tanzania)"
            value={form.location.name}
            onChange={(e) => updateLocation({ name: e.target.value })}
          />
        </Col>
        <Col span={12}>
          <Input
            placeholder="Address"
            value={form.location.address}
            onChange={(e) => updateLocation({ address: e.target.value })}
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Input
            placeholder="City"
            value={form.location.city}
            onChange={(e) => updateLocation({ city: e.target.value })}
          />
        </Col>
        <Col span={12}>
          <Input
            placeholder="Country"
            value={form.location.country}
            onChange={(e) => updateLocation({ country: e.target.value })}
          />
        </Col>
      </Row>
      {/* <Row gutter={16} style={{ marginTop: 8 }}>
      </Row> */}

      {form.listType === "PACKAGE" && (
        <Row
          align="middle"
          justify="space-between"
          style={{
            marginTop: 24,
            padding: 16,
            border: "1px solid #f0f0f0",
            borderRadius: 8,
          }}
        >
          <Col>
            <div style={{ fontWeight: 500 }}>Customizable</div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Let travelers swap components and add extras in-app.
            </Text>
          </Col>
          <Col>
            <Switch
              checked={form.customizable}
              onChange={(customizable) => update({ customizable })}
            />
          </Col>
        </Row>
      )}

      {/* Drives which traveler details the webapp's booking form asks for —
          passport/DOB etc. only when the flight is international. */}
      {form.listType === "PACKAGE" && (
        <div
          style={{
            marginTop: 16,
            padding: 16,
            border: "1px solid #f0f0f0",
            borderRadius: 8,
          }}
        >
          <div style={{ fontWeight: 500 }}>Flight</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Travelers on international flight packages are asked for passport
            details at booking.
          </Text>
          <div style={{ marginTop: 12 }}>
            <Checkbox
              checked={form.includesFlight}
              onChange={(e) =>
                update({
                  includesFlight: e.target.checked,
                  ...(e.target.checked ? {} : { isInternationalFlight: false }),
                })
              }
            >
              Includes flight
            </Checkbox>
          </div>
          <div style={{ marginTop: 8 }}>
            <Checkbox
              checked={form.isInternationalFlight}
              disabled={!form.includesFlight}
              onChange={(e) => update({ isInternationalFlight: e.target.checked })}
            >
              International flight
            </Checkbox>
          </div>
        </div>
      )}
    </div>
  );
};

export default BasicInfoStep;
